import cls from "./customInnerPage.module.scss";
import React, {FC, ReactNode, Dispatch, SetStateAction, useState} from "react";
import {IProduct, TranslationsType} from "../../../types/homePageTypes";
import {domainUrl} from "../../../helper";
import {DownloadCloudIcon} from "../../../svg";
import {useDispatch} from "react-redux";
import {setFormPopupOpen} from "../../../store/formbuilder";
import {IFormComponentType} from "../../builder/types";
import {IRequestFormDetails} from "../../../pages/[productSlug]";

interface ICustomDownloadableSideBar {
    ProductAddToCart_INTL: string;
    actionClassName: string;
    handleCardClick: () => void;
    isButtonDisabled: boolean;
    wishListButtonClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    wishIconClicked: ReactNode;
    product: IProduct;
    dbName: string;
    checkedLinks: { id: number }[];
    setCheckedLinks: Dispatch<SetStateAction<{ id: number }[]>>;
    selectedCurrSymbol: string;
    selectedCurrCode: string;
    translationsData: TranslationsType;
    requestFormInfo: IFormComponentType | null;
    requestFormDetails: IRequestFormDetails
}

const CustomDownloadableSideBar: FC<ICustomDownloadableSideBar> = (
    {
        ProductAddToCart_INTL,
        actionClassName,
        handleCardClick,
        isButtonDisabled,
        wishListButtonClick,
        wishIconClicked,
        product,
        dbName,
        setCheckedLinks,
        checkedLinks,
        selectedCurrSymbol,
        selectedCurrCode,
        translationsData,
        requestFormDetails,
        requestFormInfo
    }
) => {
    const dispatch = useDispatch();
    const Button = (
        {className = "", title = ProductAddToCart_INTL, onClick = () => {
            if (checkedLinks.length > 0) {
                handleCardClick()
                setRequired(false)
            } else {
                setRequired(true)
            }
        }, isButtonDisabledInside = false}
            : {className?: string, title?: string, onClick?: () => any, isButtonDisabledInside?: boolean}
    ) => {
        return <div
            className={`${cls["product__actions-item"]} ${cls["product__actions-item--addToCart"]}`}>
            <div className={`${cls["product__actions-item"]} ${cls["product__actions-item--addToCart"]} ${actionClassName}`}>
                <button
                    onClick={async (e) => {
                        e.preventDefault();
                        await onClick()
                    }}
                    disabled={isButtonDisabledInside}
                    className={`btn btn-orange ${cls["inner-addToCart"]} ${cls["rounded-pills"]} btn-lg ${className}`}
                >
                    {title}
                </button>
            </div>
        </div>
    }
    const [required, setRequired] = useState(false);

    const validationMessage = {
        "true": <p className={cls["required__text"]}>{translationsData.LinksRequired}</p>,
        "false": null
    }

    const sampleLinks = {
        "true": <>
            <span className={cls["links__heading"]}>{translationsData.Samples}</span>
            <div className={cls["product__samples"]}>
                {product.downloadable_samples.map((item, index) => {
                        return (
                            <div className={cls["sample__link"]} key={index}>
                                <a href={domainUrl(`${dbName}/api/downloadable/download-sample/sample/${item.id}`)}>
                                    <div className={cls["sample__links__items"]}>
                                        <DownloadCloudIcon/>
                                        {item.title}
                                    </div>
                                </a>
                            </div>
                        );
                    })}
            </div>
        </>,
        "false": null
    };

    const productLinks = (
        <div className={cls["product__links"]}>
            {product.downloadable_links.map(({id, ...item}, index) => {

                const isSamples = item.sample_url || item.sample_file_url !== null;
                const positionSymbolHandle = () => {
                    if (selectedCurrCode === "AMD") {
                        return `${item.price}${selectedCurrSymbol}`
                    }
                    return `${selectedCurrSymbol}${item.price}`
                }
                const price = {
                    "true": `+ ${positionSymbolHandle()}`,
                    "false": null
                };

                const handleCheckLinksChane = () => {
                    setCheckedLinks((prevState) => {
                        let updatedStateArray = [...prevState];
                        const findElem = updatedStateArray.some(({id: someId}) => id === someId);
                        if (findElem) {
                            return updatedStateArray.filter(({id: filterId}) => filterId !== id);
                        } else {
                            updatedStateArray.push({id});
                        }

                        setRequired(updatedStateArray.length === 0);
                        return updatedStateArray;
                    })
                }

                return (
                    <div
                        key={index}
                        className={cls["product__variant"]}
                    >
                        <div className={cls[`variant__link`]}>
                        <label>
                                <input
                                    type="checkbox"
                                    onChange={handleCheckLinksChane}
                                />
                                <div className={cls["variant__sample"]}>
                                    <span>{item.title} {price[`${item.price !> 0}`]}</span>
                                </div>
                        </label>
                            {isSamples ?
                                <a href={domainUrl(`${dbName}/api/downloadable/download-sample/link/${id}`)} className={cls["links__sample__items"]}>
                                    <DownloadCloudIcon/>
                                    <span>{translationsData.Sample}</span>
                                </a>
                                : null
                            }
                        </div>
                    </div>
                );
                })}
        </div>
    );

    return (
        <div className={cls["product__sidebar"]}>
            <form className={cls["product__options"]}>
                <div className={`form-group ${cls["product__option"]}`}>
                    <div className={cls["downloadable__links"]}>
                        <div className={cls["product__samples_container"]}>
                            {sampleLinks[`${product.downloadable_samples.length > 0}`]}
                        </div>
                        <div className={cls["product__links_container"]}>
                            <span className={cls["links__heading"]}>{translationsData.Links}</span>
                            {productLinks}
                            {validationMessage[`${required}`]}
                        </div>
                    </div>
                    <div className={cls["product__actions"]}>
                        <div className={cls["inner-buttons-wrapper"]}>
                            <Button isButtonDisabledInside={isButtonDisabled} />
                            {Number(requestFormDetails?.requestFormActive) && requestFormDetails?.requestFormBtn ? (
                                <Button
                                    isButtonDisabledInside={false}
                                    className={"inner-request-form-button"}
                                    title={requestFormDetails?.requestFormBtn}
                                    onClick={() => dispatch(setFormPopupOpen({id: requestFormInfo?.data?.id, open: true}))}
                                />
                            ) : null}
                        </div>
                        <div
                            className={`${cls["product__actions-item"]} ${cls["product__actions-item--wishlist"]} ${cls["liked-button-wrapper"]}`}>
                            <span>
                                <button
                                    aria-label="Wish list"
                                    title="Wishlist"
                                    onClick={wishListButtonClick}
                                    className="btn-light btn-svg-icon btn-svg-icon--fake-svg"
                                >
                                    <div className={cls["wishlist-icon-wrapper"]}>
                                        {wishIconClicked}
                                    </div>
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
};

export default CustomDownloadableSideBar;
